<template>
  <div class="webhook-container card shadow-sm">
    <div class="card-header bg-white py-3">
      <h5 class="mb-1 fw-bold">Configuration des webhooks</h5>
      <p class="text-muted small mb-0">
        Mise en place d’un webhook permettant la synchronisation en temps réel
        des statuts des colis avec des services externes.
      </p>
    </div>

    <div class="card-body">
      <div class="d-flex flex-wrap gap-2 mb-3">
        <span class="badge bg-light text-dark border px-3 py-2">
          <i class="bi bi-send-fill me-1 text-primary"></i> Method: <b>POST</b>
        </span>
        <span class="badge bg-light text-dark border px-3 py-2">
          <i class="bi bi-code-slash me-1 text-primary"></i> Content-Type: <b>application/json</b>
        </span>
      </div>

      <div class="mb-4">
        <div class="p-3 bg-light rounded border border-info-subtle">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0 fw-bold"><i class="bi bi-info-circle-fill text-info me-2"></i>Documentation Technique</h6>
            <!-- <a class="btn btn-sm btn-link text-primary fw-bold text-decoration-none p-0" 
               href="https://api.kargoexpress.ma/d_status" 
               target="_blank">
              <i class="bi bi-box-arrow-up-right me-1"></i>Liste des statuts
            </a> -->
          </div>
          
          <p class="small text-muted mb-2">Payload JSON envoyé à votre URL :</p>
          
          <div class="table-responsive bg-white rounded shadow-sm border">
            <table class="table table-sm table-hover mb-0">
              <thead class="table-light">
                <tr class="small">
                  <th class="ps-3">Clé (Key)</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody class="small">
                <tr>
                  <td class="ps-3"><code class="text-danger">codeColis</code></td>
                  <td class="text-secondary">Le numéro de suivi unique</td>
                </tr>
                <tr>
                  <td class="ps-3"><code class="text-danger">status</code></td>
                  <td class="text-secondary">Le statut actuel de l'envoi</td>
                </tr>
                <tr>
                  <td class="ps-3"><code class="text-danger">situation</code></td>
                  <td class="text-secondary">Situation géographique ou étape</td>
                </tr>
                <tr>
                  <td class="ps-3"><code class="text-danger">note</code></td>
                  <td class="text-secondary">Commentaire ou motif</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <form @submit.prevent="addWebhook" class="row g-2 mb-4">
        <div class="col-md-9">
          <div class="input-group">
            <span class="input-group-text bg-light"
              ><i class="bi bi-link-45deg"></i
            ></span>
            <input
              v-model="url"
              type="url"
              required
              class="form-control"
              placeholder="https://your-api.com/webhook-endpoint"
              :disabled="isLoading"
            />
          </div>
        </div>
        <div class="col-md-3">
          <button class="btn btn-primary w-100" :disabled="isLoading || !url">
            <span
              v-if="isLoading"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            {{ isLoading ? "Enregistrement..." : "Ajouter un endpoint" }}
          </button>
        </div>
      </form>

      <hr class="my-4 opacity-25" />

      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th class="border-0">URL cible</th>
              <th class="border-0 text-end" width="150">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="hook in webhooks" :key="hook.id">
              <td class="text-break font-monospace small text-primary">
                {{ hook.url }}
              </td>
              <td class="text-end">
                <button
                  class="btn btn-outline-danger btn-sm border-0"
                  @click="confirmDelete(hook.id)"
                  title="Supprimer le webhook"
                >
                  <i class="bi bi-trash3 me-1"></i> Supprimer
                </button>
              </td>
            </tr>

            <tr v-if="webhooks.length === 0 && !isLoading">
              <td colspan="2" class="text-center py-5">
                <div class="text-muted">
                  <i class="bi bi-plug-fill display-6 mb-2 d-block"></i>
                  <p>Aucun webhook configuré pour le moment.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useAppToast } from "@/composables/useAppToast";
import api from "@/utils/axios";
import Swal from "sweetalert2";

const { showToast } = useAppToast();
const webhooks = ref([]);
const url = ref("");
const isLoading = ref(false);

async function fetchWebhooks() {
  try {
    const res = await api.get("/api/auth/client/webhooks");
    webhooks.value = res.data;
  } catch (e) {
    showToast("Failed to load webhooks", "error");
  }
}

async function addWebhook() {
  if (!url.value) return;

  isLoading.value = true;
  try {
    await api.post("/api/auth/client/webhooks", { url: url.value });
    showToast("Webhook endpoint added successfully", "success");
    url.value = "";
    await fetchWebhooks();
  } catch (e: any) {
    showToast(e.response?.data?.message || "Error adding webhook", "error");
  } finally {
    isLoading.value = false;
  }
}

async function confirmDelete(id: number) {
  Swal.fire({
    text: "Êtes-vous sûr de vouloir supprimer ce webhook ? Vous ne recevrez plus d'événements à cette URL.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Oui, supprimer!",
  }).then(async (result) => {
    if (result.isConfirmed) {
      await api.delete(`/api/webhooks/${id}`);
    }
  });
}

async function removeWebhook(id: number) {
  //   try {
  //     await webhookService.remove(id);
  //     showToast("Webhook removed", "success");
  //     fetchWebhooks();
  //   } catch (e) {
  //     showToast("Error deleting webhook", "error");
  //   }
}

onMounted(fetchWebhooks);
</script>

<style scoped>
.webhook-container {
  border-radius: 12px;
  overflow: hidden;
}
.input-group-text {
  border-right: none;
}
.form-control:focus {
  box-shadow: none;
  border-color: #dee2e6;
}
.table th {
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  color: #6c757d;
}
</style>